'use client';

import { useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowRight, Check, Loader2, MapPin, Search, SlidersHorizontal } from 'lucide-react';
import { MapWallMap, type MapView } from './mapwall-map';
import { mapWallFormats, mapWallStyles, formatCoordinates, type MapWallFormatId, type MapWallOrientation, type MapWallStyleId } from '@/lib/mapwall';

type SearchResult = {
  id: string;
  placeName: string;
  country: string;
  lat: number;
  lng: number;
  bbox?: [number, number, number, number];
  type?: string;
};

const styleIds = Object.keys(mapWallStyles) as MapWallStyleId[];
const formatIds = Object.keys(mapWallFormats) as MapWallFormatId[];

function posterRatio(format: MapWallFormatId, orientation: MapWallOrientation) {
  const base = mapWallFormats[format];
  const width = orientation === 'landscape' && format !== 'square' ? base.heightMm : base.widthMm;
  const height = orientation === 'landscape' && format !== 'square' ? base.widthMm : base.heightMm;
  return `${width} / ${height}`;
}

function zoomForType(type?: string) {
  const normalized = String(type || '').toLowerCase();
  if (/(country|nation|state|region)/.test(normalized)) return 6;
  if (/(island|county|province)/.test(normalized)) return 8;
  if (/(neighbourhood|suburb|street|address)/.test(normalized)) return 13;
  return 11;
}

export function MapWallEditor({ locale = 'nl', compact = false }: { locale?: string; compact?: boolean }) {
  const router = useRouter();
  const [query, setQuery] = useState('Amsterdam');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selected, setSelected] = useState<SearchResult>({
    id: 'default-amsterdam',
    placeName: 'Amsterdam',
    country: 'Nederland',
    lat: 52.3676,
    lng: 4.9041,
    type: 'city',
  });
  // What the customer framed on screen; the print file is rendered on exactly this.
  const [view, setView] = useState<MapView>({ lat: 52.3676, lng: 4.9041, zoom: 11 });
  const [style, setStyle] = useState<MapWallStyleId>('minimal');
  const [format, setFormat] = useState<MapWallFormatId>('a3');
  const [orientation, setOrientation] = useState<MapWallOrientation>('portrait');
  const [title, setTitle] = useState('Amsterdam');
  const [subtitle, setSubtitle] = useState('Nederland');
  const [showCoordinates, setShowCoordinates] = useState(true);
  const [searching, setSearching] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState('');

  const styleConfig = mapWallStyles[style];
  // Follow the framed view, so the printed coordinates match what is shown.
  const coordinates = useMemo(() => formatCoordinates(view.lat, view.lng), [view.lat, view.lng]);

  async function searchPlaces() {
    const clean = query.trim();
    if (clean.length < 2) return;
    setError('');
    setSearching(true);
    try {
      const response = await fetch(`/api/maps/search?q=${encodeURIComponent(clean)}&locale=${encodeURIComponent(locale)}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Zoeken mislukt');
      setResults(data.results || []);
      const first = data.results?.[0];
      if (first) choosePlace(first);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Locatie zoeken is mislukt.');
    } finally {
      setSearching(false);
    }
  }

  function choosePlace(place: SearchResult) {
    setSelected(place);
    setView({ lat: place.lat, lng: place.lng, zoom: zoomForType(place.type) });
    setTitle(place.placeName);
    setSubtitle(place.country);
  }

  async function generatePoster() {
    setError('');
    setRendering(true);
    try {
      const response = await fetch('/api/maps/render', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          placeName: selected.placeName,
          country: selected.country,
          lat: view.lat,
          lng: view.lng,
          zoom: view.zoom,
          type: selected.type,
          style,
          format,
          orientation,
          title,
          subtitle,
          showCoordinates,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Kaartposter genereren is mislukt.');
      router.push(`/${locale}/producten/map-poster?generatedUploadId=${encodeURIComponent(data.uploadId)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kaartposter genereren is mislukt.');
    } finally {
      setRendering(false);
    }
  }

  const preview = (
    <div
      className={`mx-auto w-full border shadow-2xl ${compact ? 'max-w-[420px]' : 'max-w-[560px]'}`}
      style={{
        aspectRatio: posterRatio(format, orientation),
        background: styleConfig.background,
        borderColor: styleConfig.border,
        color: styleConfig.text,
      }}
    >
      <div className="flex h-full flex-col p-[7%]">
        <div className="relative flex-1 overflow-hidden border" style={{ background: styleConfig.panel, borderColor: styleConfig.border }}>
          <MapWallMap
            lat={view.lat}
            lng={view.lng}
            zoom={view.zoom}
            styleId={style}
            onViewChange={setView}
          />
          <div className="pointer-events-none absolute bottom-2 right-2 rounded bg-white/70 px-2 py-0.5 text-[9px] font-medium text-slate-600">
            &copy; OpenStreetMap
          </div>
        </div>
        <div className="flex min-h-[20%] flex-col items-center justify-center text-center">
          <div className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-5xl'} max-w-full text-balance break-words font-black uppercase tracking-normal`} style={{ color: styleConfig.text }}>
            {title || selected.placeName}
          </div>
          <div className={`${compact ? 'text-[10px] md:text-xs' : 'text-xs md:text-sm'} mt-3 max-w-full truncate font-semibold uppercase tracking-[0.22em]`} style={{ color: styleConfig.muted }}>
            {[subtitle, showCoordinates ? coordinates : ''].filter(Boolean).join('  |  ')}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={compact ? '' : 'min-h-screen bg-slate-950 text-white'}>
      <div className={compact ? '' : 'container mx-auto px-4 py-8 lg:py-12'}>
        <div className={compact ? 'grid gap-5 xl:grid-cols-[minmax(260px,0.86fr)_360px] xl:items-start' : 'grid gap-8 lg:grid-cols-[1fr_420px] lg:items-start'}>
          <div className={compact ? 'rounded-2xl bg-slate-950 p-4' : 'lg:sticky lg:top-6'}>
            {preview}
          </div>

          <div className={`rounded-2xl border border-slate-200 bg-white text-slate-950 shadow-xl ${compact ? 'p-4' : 'p-4 md:p-6'}`}>
            <div className="mb-5 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-slate-900" />
              <h2 className="text-xl font-black">Maak je kaartposter</h2>
            </div>

            <div className="space-y-5">
              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">Stad, land, eiland of adres</label>
                <div className="flex gap-2">
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') searchPlaces();
                    }}
                    className="h-12 min-w-0 flex-1 rounded-lg border border-slate-300 px-3 text-base outline-none ring-slate-900 focus:ring-2"
                    placeholder="Bijv. Amsterdam, Ibiza, Italie"
                  />
                  <button
                    type="button"
                    onClick={searchPlaces}
                    disabled={searching}
                    className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-950 text-white disabled:opacity-60"
                    aria-label="Zoeken"
                  >
                    {searching ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
                  </button>
                </div>
                {results.length > 0 && (
                  <div className="mt-2 max-h-44 overflow-auto rounded-lg border border-slate-200 bg-white">
                    {results.map((result) => (
                      <button
                        key={result.id}
                        type="button"
                        onClick={() => choosePlace(result)}
                        className="flex w-full items-center justify-between gap-3 border-b border-slate-100 px-3 py-2 text-left last:border-b-0 hover:bg-slate-50"
                      >
                        <span>
                          <span className="block font-bold">{result.placeName}</span>
                          <span className="block text-xs text-slate-500">{result.country}</span>
                        </span>
                        {selected.id === result.id && <Check className="h-4 w-4 text-emerald-600" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-bold text-slate-700">Titel</label>
                  <input value={title} onChange={(event) => setTitle(event.target.value)} className="h-11 w-full rounded-lg border border-slate-300 px-3 outline-none ring-slate-900 focus:ring-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-bold text-slate-700">Subtitel</label>
                  <input value={subtitle} onChange={(event) => setSubtitle(event.target.value)} className="h-11 w-full rounded-lg border border-slate-300 px-3 outline-none ring-slate-900 focus:ring-2" />
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700">
                  <SlidersHorizontal className="h-4 w-4" />
                  Stijl
                </div>
                <div className={`grid gap-2 ${compact ? '' : 'sm:grid-cols-2'}`}>
                  {styleIds.map((id) => {
                    const item = mapWallStyles[id];
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setStyle(id)}
                        className={`rounded-lg border p-3 text-left transition ${compact ? 'min-h-12' : 'min-h-16'} ${style === id ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-white hover:border-slate-400'}`}
                      >
                        <span className="block font-black">{item.name}</span>
                        {!compact && <span className={`mt-1 block text-xs leading-5 ${style === id ? 'text-slate-300' : 'text-slate-500'}`}>{item.description}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-bold text-slate-700">Formaat</label>
                  <select value={format} onChange={(event) => setFormat(event.target.value as MapWallFormatId)} className="h-11 w-full rounded-lg border border-slate-300 px-3">
                    {formatIds.map((id) => <option key={id} value={id}>{mapWallFormats[id].label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-bold text-slate-700">Orientatie</label>
                  <select value={orientation} onChange={(event) => setOrientation(event.target.value as MapWallOrientation)} className="h-11 w-full rounded-lg border border-slate-300 px-3">
                    <option value="portrait">Staand</option>
                    <option value="landscape">Liggend</option>
                  </select>
                </div>
              </div>

              <label className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm font-bold">
                <input type="checkbox" checked={showCoordinates} onChange={(event) => setShowCoordinates(event.target.checked)} className="h-4 w-4" />
                Coordinaten tonen
              </label>

              {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-700">{error}</div>}

              <button
                type="button"
                onClick={generatePoster}
                disabled={rendering}
                className="flex h-12 w-full items-center justify-center rounded-xl bg-slate-950 px-5 py-4 font-black text-white transition hover:bg-slate-800 disabled:opacity-60"
              >
                {rendering ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <ArrowRight className="mr-2 h-5 w-5" />}
                Genereer printbestand en bestel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
